import React, { useCallback, useEffect, useRef, useState } from "react";

import Popover from "@material-ui/core/Popover";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";

import { BottomSheet } from "shared/ui/BottomSheet";
import { SearchInput } from "shared/ui/SearchInput";
import { ChevronDownIcon } from "shared/ui/icons";

import {
    ClearOption,
    LoadMoreAnchor,
    Message,
    Option,
    Options,
    Panel,
    PopoverPaper,
    Trigger,
    TriggerLabel,
} from "./styles";

interface SearchableSelectProps<T> {
    value: T | null;
    onChange: (value: T | null) => void;
    options: T[];
    getOptionKey: (option: T) => string;
    getOptionLabel: (option: T) => string;
    // Строка списка может быть богаче подписи в поле (название + цена в две колонки).
    renderOption?: (option: T) => React.ReactNode;

    query: string;
    onQueryChange: (query: string) => void;
    // Открытие — сигнал загрузить первую страницу: тянуть каталог, пока поле не тронули, незачем.
    onOpen?: () => void;

    loading?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;

    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    // Пункт «ничего не выбрано»; без подписи очистка недоступна.
    clearLabel?: string;
    disabled?: boolean;
    id?: string;
    title?: string;
}

/*
* Селект с поиском и постраничной подгрузкой. Данные грузит вызывающий (query и loadMore —
* наружу), примитив отвечает только за раскладку и поведение: на десктопе поповер у поля,
* на узком вьюпорте нижний лист — как у ActionMenu.
* */
export function SearchableSelect<T>({
    value,
    onChange,
    options,
    getOptionKey,
    getOptionLabel,
    renderOption,
    query,
    onQueryChange,
    onOpen,
    loading,
    hasMore,
    onLoadMore,
    placeholder = "Не выбрано",
    searchPlaceholder = "Поиск",
    emptyText = "Ничего не найдено",
    clearLabel,
    disabled,
    id,
    title,
}: SearchableSelectProps<T>) {
    const theme = useTheme();
    const isWide = useMediaQuery(theme.breakpoints.up("sm"));
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const [open, setOpen] = useState<boolean>(false);
    const [scrollNode, setScrollNode] = useState<HTMLDivElement | null>(null);
    const [anchorNode, setAnchorNode] = useState<HTMLDivElement | null>(null);

    const openPanel = () => {
        setOpen(true);
        if (onOpen) onOpen();
    };

    const closePanel = useCallback(() => setOpen(false), []);

    const select = (option: T | null) => {
        onChange(option);
        closePanel();
    };

    /*
    * Догрузка по прокрутке списка. В зависимостях — число уже показанных строк: наблюдатель
    * сообщает о *смене* видимости, и без переподписки якорь, оставшийся в зоне видимости после
    * догрузки, второй раз не сработает (та же ловушка, что в списке позиций меню).
    * */
    useEffect(() => {
        if (!scrollNode || !anchorNode || !hasMore || !onLoadMore || loading) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
            },
            { root: scrollNode }
        );

        observer.observe(anchorNode);

        return () => observer.disconnect();
    }, [scrollNode, anchorNode, hasMore, onLoadMore, loading, options.length]);

    const selectedKey = value ? getOptionKey(value) : null;

    const renderPanel = (inSheet: boolean) => (
        <Panel $inSheet={inSheet}>
            <SearchInput value={query} placeholder={searchPlaceholder} onChange={onQueryChange} />

            <Options $inSheet={inSheet} ref={setScrollNode}>
                {clearLabel && (
                    <ClearOption
                        type="button"
                        $selected={!value}
                        $inSheet={inSheet}
                        onClick={() => select(null)}
                    >
                        {clearLabel}
                    </ClearOption>
                )}

                {options.map((option) => (
                    <Option
                        key={getOptionKey(option)}
                        type="button"
                        $selected={getOptionKey(option) === selectedKey}
                        $inSheet={inSheet}
                        onClick={() => select(option)}
                    >
                        {renderOption ? renderOption(option) : getOptionLabel(option)}
                    </Option>
                ))}

                {loading && <Message>Загрузка…</Message>}
                {!loading && options.length === 0 && <Message>{emptyText}</Message>}

                {hasMore && <LoadMoreAnchor ref={setAnchorNode} />}
            </Options>
        </Panel>
    );

    return (
        <>
            <Trigger
                id={id}
                ref={triggerRef}
                type="button"
                disabled={disabled}
                $placeholder={!value}
                onClick={openPanel}
            >
                <TriggerLabel>{value ? getOptionLabel(value) : placeholder}</TriggerLabel>
                <ChevronDownIcon />
            </Trigger>

            {isWide ? (
                <Popover
                    open={open}
                    anchorEl={triggerRef.current}
                    onClose={closePanel}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{
                        style: {
                            ...PopoverPaper,
                            width: triggerRef.current ? triggerRef.current.offsetWidth : undefined,
                        },
                    }}
                >
                    {renderPanel(false)}
                </Popover>
            ) : (
                <BottomSheet open={open} title={title} onClose={closePanel}>
                    {renderPanel(true)}
                </BottomSheet>
            )}
        </>
    );
}
